import { useState, type FC } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { Avatar, Box, Chip as MuiChip, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { ExtendedForm, FormContextData } from "@thalamos/aspire-common";
import {
  baseTemplates,
  INVITATION_WITHDRAWN_REASON,
} from "@thalamos/aspire-common";

import {
  Banner,
  BannerList,
  Button,
  FormTitle,
  PopupDialog,
  PopupDialogTitle,
  ReadOnlyContent,
} from "~/components/design-system/index.js";
import { Container, ExpandMore } from "~/components/layout/styleWrappers.js";

interface ParticipantsListProps {
  formContext: FormContextData;
}

export function ParticipantsList({ formContext }: ParticipantsListProps) {
  const [showParticipants, setShowParticipants] = useState(true);
  const { t } = useTranslation();

  const [selectedChipIndex, setSelectedChipIndex] = useState<number | null>(
    null,
  );

  const isAdmission = formContext.type === "admission";

  return (
    <>
      <Container addMarginBottom>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            mb: 1.5,
          }}
        >
          <FormTitle
            subtitleText={t("pages.formPage.participants")}
            hasContainerMarginBottom={false}
            subTitleTextFontSize={2}
          />
          <ExpandMore
            role="button"
            isExpanded={showParticipants}
            onClick={() => {
              setShowParticipants(!showParticipants);
            }}
            aria-expanded={showParticipants}
            aria-label={t("components.cardExtended.expandMoreLabel")}
          >
            <ExpandMoreIcon sx={{ color: "primary.main" }} />
          </ExpandMore>
        </Box>

        {showParticipants && (
          <Box>
            {/* Regular participants (work items) */}
            {formContext.workItems && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 0.5,
                }}
              >
                {formContext.workItems.map((w, index) => {
                  const isRejectedOrAdmissionCompleted =
                    w.status === "rejected" ||
                    (isAdmission && w.status === "completed" && w.reason) ||
                    (w.team.type === "amhp" && formContext.status === "closed");

                  const isClickableChip =
                    isRejectedOrAdmissionCompleted ||
                    (isAdmission && !!w.reason && w.status === "rejected");

                  return (
                    <ParticipantChip
                      key={`workitem-${index}`}
                      workItem={w}
                      isClickable={!!isClickableChip}
                      onClick={() => {
                        setSelectedChipIndex(index);
                      }}
                      isAdmission={isAdmission}
                      formContext={formContext}
                      setSelectedChipIndex={setSelectedChipIndex}
                      selectedChipIndex={selectedChipIndex!}
                      index={index}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </Container>
    </>
  );
}

interface ParticipantChipProps {
  workItem: ExtendedForm<any>["workItems"][0];
  isClickable: boolean;
  onClick: () => void;
  setSelectedChipIndex: (params: any) => void;
  selectedChipIndex: number;
  index: number;
  isAdmission: boolean;
  formContext: FormContextData;
}

const ParticipantChip: FC<ParticipantChipProps> = ({
  workItem,
  isClickable,
  onClick,
  setSelectedChipIndex,
  selectedChipIndex,
  index,
  isAdmission,
  formContext,
}) => {
  function getFormTemplateById(formId: string | null) {
    const form = formContext.forms.find((form) => form.id === formId);
    return form && form.formTemplate;
  }

  const formTemplate = baseTemplates.find((t) =>
    formContext.workItems
      .map((workItem) => getFormTemplateById(workItem.formId))
      .filter((template) => template)
      .find((template) => template?.id.includes(t.id)),
  );

  const readOnlyContentFn = (
    workItem: ExtendedForm<any>["workItems"][0],
  ): {
    reasonContent: string[];
    descriptionContent: string[];
  } => {
    if (
      isAdmission &&
      formContext.status === "closed" &&
      formContext.closedReason &&
      workItem.team.type === "amhp"
    ) {
      return {
        reasonContent: [formContext.closedReason],
        descriptionContent: [formContext.closedReasonDescription || ""],
      };
    }

    if (isAdmission && workItem.status === "completed" && workItem.reason) {
      return {
        reasonContent: [workItem.reason],
        descriptionContent: [workItem.reasonDescription || ""],
      };
    }

    if (workItem.status === "rejected" && workItem.rejectedReason) {
      return {
        reasonContent: [workItem.rejectedReason],
        descriptionContent: [workItem.rejectedReasonDescription || ""],
      };
    }
    return { reasonContent: [], descriptionContent: [] };
  };

  const generateLabel = (workItem: ExtendedForm<any>["workItems"][0]) => {
    const teamName = workItem.team.name;

    if (workItem.status === "completed" || workItem.status === "rejected") {
      if (
        isAdmission &&
        formContext.status === "closed" &&
        formContext.closedReason &&
        workItem.team.type === "amhp"
      ) {
        return teamName + ` (action confirmed: ${formContext.closedReason})`;
      }

      if (isAdmission && workItem.status === "completed" && workItem.reason) {
        return teamName + ` (action confirmed: ${workItem.reason})`;
      }

      if (workItem.rejectedReason && workItem.status === "rejected") {
        return teamName + ` (declined: ${workItem.rejectedReason})`;
      }
    }

    return teamName;
  };

  const isAdmissionType = formContext.type === "admission";
  const isClosedAdmission =
    isAdmissionType &&
    formContext.status === "closed" &&
    formContext.closedReason;
  const isCompletedWorkItem =
    isAdmissionType && workItem.status === "completed" && workItem.reason;

  const chipColor =
    isClosedAdmission || isCompletedWorkItem || workItem.status === "rejected"
      ? "error"
      : "default";
  const theme = useTheme();
  const isRejectedOrCompletedStatus = (
    work: ExtendedForm<any>["workItems"][0],
  ) => work.status === "completed" || work.status === "rejected";
  return (
    <Box
      sx={{
        display: "flex",
      }}
    >
      <Box onClick={isClickable ? onClick : undefined}>
        <MuiChip
          clickable={isClickable}
          sx={{
            mr: theme.spacing(1),
            my: 1,
          }}
          style={{
            backgroundColor:
              chipColor === "error"
                ? theme.palette.error.main
                : theme.palette.common.lightGray,
            ...(chipColor === "error" && { color: "white" }),
          }}
          avatar={
            <Avatar
              sx={{
                ...(chipColor === "default" && {
                  backgroundColor: "common.royalBlue",
                }),
              }}
            >
              <Box
                sx={{
                  ...(chipColor === "default" && { color: "common.white" }),
                }}
              >
                {workItem.team.name
                  .split(" ")
                  .slice(0, 2)
                  .map((x) => x.slice(0, 1))}
              </Box>
            </Avatar>
          }
          label={generateLabel(workItem)}
          onDelete={isClickable ? onClick : undefined}
          deleteIcon={
            workItem.status === "rejected" ? (
              <RemoveRedEyeOutlinedIcon sx={{ color: "black" }} />
            ) : (
              <></>
            )
          }
        />
      </Box>
      {isRejectedOrCompletedStatus(workItem) && selectedChipIndex === index && (
        <ParticipantPopupDialog
          workItem={workItem}
          generateLabel={generateLabel}
          setSelectedChipIndex={setSelectedChipIndex}
          selectedChipIndex={selectedChipIndex}
          index={index}
          isAdmission={isAdmission}
          formTemplate={formTemplate}
          readOnlyContentFn={readOnlyContentFn}
        />
      )}
    </Box>
  );
};

export interface ParticipantPopupDialogProps {
  workItem: ExtendedForm<any>["workItems"][0];
  generateLabel: (...params: any) => string;
  setSelectedChipIndex: (params: any) => void;
  selectedChipIndex: number;
  index: number;
  isAdmission: boolean;
  formTemplate: any;
  readOnlyContentFn: (params: any) => {
    reasonContent: string[];
    descriptionContent: string[];
  };
}

export const ParticipantPopupDialog: FC<ParticipantPopupDialogProps> = ({
  workItem,
  setSelectedChipIndex,
  selectedChipIndex,
  isAdmission,
  formTemplate,
  readOnlyContentFn,
}) => {
  const { t } = useTranslation();

  const isWithdrawn =
    workItem.status === "rejected" &&
    workItem.rejectedReason === INVITATION_WITHDRAWN_REASON;

  const bannerTitle = () => {
    if (isAdmission && workItem.status !== "rejected") {
      if (workItem.team.type === "amhp") {
        return `${workItem.team.name} has confirmed that an application form will not be made. Everyone involved in the assessment should discuss how to proceed.`;
      } else {
        return `${workItem.team.name} has confirmed that a medical recommendation form will not be made. Everyone involved in the assessment should discuss how to proceed.`;
      }
    }
    if (isWithdrawn) {
      return `The invitation to ${workItem.team.name} has been withdrawn. They no longer have access to this patient's assessment. To grant access again, please send the form to them again.`;
    }
    return `The invite you sent to ${workItem.team.name} ${
      formTemplate?.category === "admission" || !formTemplate
        ? "to access this patient's assessment documents"
        : `to complete form ${formTemplate.formName}: ${
            formTemplate.description || `Section ${formTemplate.section}`
          }`
    } was declined. Everyone involved in the assessment should discuss how to proceed.`;
  };
  return (
    <PopupDialog
      onClose={() => {
        setSelectedChipIndex(null);
      }}
      open={selectedChipIndex !== null}
    >
      <PopupDialogTitle
        titleText={t(
          isWithdrawn
            ? "pages.formPage.participantsList.popupDialog.titleWithdrawn"
            : isAdmission && workItem.status !== "rejected"
              ? workItem.team.type === "amhp"
                ? "pages.formPage.participantsList.popupDialog.admissionTitleNoApp"
                : "pages.formPage.participantsList.popupDialog.admissionTitleNoMedRec"
              : "pages.formPage.participantsList.popupDialog.title",
        )}
        closeDialog={() => {
          setSelectedChipIndex(null);
        }}
      />
      <Box sx={{ mb: 3 }}>
        <Banner title={bannerTitle()} bannerType={BannerList.WARNING} />
      </Box>
      {!isWithdrawn && (
        <>
          <ReadOnlyContent
            label={t(
              isAdmission && workItem.status !== "rejected"
                ? workItem.team.type === "amhp"
                  ? "pages.formPage.participantsList.popupDialog.admissionReadOnlyContentTitleNoApp"
                  : "pages.formPage.participantsList.popupDialog.admissionReadOnlyContentTitleMedRec"
                : "pages.formPage.participantsList.popupDialog.readOnlyContentTitle",
            )}
            content={readOnlyContentFn(workItem).reasonContent}
            isActive={true}
            useMarginBottom
          />
          {readOnlyContentFn(workItem).descriptionContent[0] && (
            <ReadOnlyContent
              label={
                workItem.status === "rejected" &&
                workItem.rejectedReason &&
                workItem.rejectedReason.includes("incorrect")
                  ? t(
                      "pages.formPage.participantsList.popupDialog.readOnlyIncorrectDetailsTitle",
                    )
                  : t(
                      "pages.formPage.participantsList.popupDialog.readOnlyAdditionalInfoTitle",
                    )
              }
              content={readOnlyContentFn(workItem).descriptionContent}
              isActive={true}
            />
          )}
        </>
      )}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          mt: 2,
        }}
      >
        <Button
          variant="contained"
          label={t("buttonLabels.close")}
          onClick={() => {
            setSelectedChipIndex(null);
          }}
        />
      </Box>
    </PopupDialog>
  );
};
